import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { studentApi, batchApi, apiErrorMessage } from '../services/api';
import { useOrg } from '../context/OrgContext';
import { useToast } from '../context/ToastContext';
import { Button, Card, Metric, PageSkeleton, ErrorState, EmptyState, Field } from '../components/ui';
import { formatScore } from '../utils/results';

// One student: who they are, which batch they are in, and how they have done
// across this institute's tests.
export default function StudentDetailPage() {
  const { userId } = useParams();
  const org = useOrg();
  const toast = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingBatch, setSavingBatch] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([studentApi.attempts(org.id, userId), batchApi.list(org.id)])
      .then(([res, batchesRes]) => {
        setData(res.data);
        setBatches(batchesRes.data || []);
        setError(null);
      })
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load this student')))
      .finally(() => setLoading(false));
  }, [org.id, userId]);

  useEffect(() => { load(); }, [load]);

  const changeBatch = async (batchId) => {
    setSavingBatch(true);
    try {
      await studentApi.setBatch(org.id, userId, batchId || null);
      const batchName = batches.find((b) => b.id === batchId)?.name || null;
      setData((d) => ({ ...d, student: { ...d.student, batchId: batchId || null, batchName } }));
      toast.success(batchId ? `Moved to ${batchName}.` : 'Removed from batch.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to change batch'));
    } finally {
      setSavingBatch(false);
    }
  };

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <ErrorState message="Student not found." />;

  const { student, summary, attempts } = data;
  const initials = (student.userName || student.userEmail || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  return (
    <>
      <p className="breadcrumb"><Button variant="text" onClick={() => navigate('/students')}>← All students</Button></p>
      <div className="student-head">
        <span className="avatar">{initials}</span>
        <div>
          <h2 style={{ margin: 0 }}>{student.userName}</h2>
          <small>{student.userEmail}</small>
          <small>
            <span className={student.status === 'active' ? 'status' : 'pending'}>{student.status === 'active' ? 'Active' : 'Pending'}</span>
          </small>
        </div>
      </div>

      <div className="metrics">
        <Metric label="Tests taken" value={summary.testsTaken} hint={`${attempts.length} attempt${attempts.length === 1 ? '' : 's'} in total`} />
        <Metric label="Average score" value={formatScore(summary.averageScore)} hint={summary.averageScore == null ? 'No completed tests yet' : 'Across completed tests'} />
        <Metric label="Best score" value={formatScore(summary.bestScore)} hint=" " />
        <Card className="metric">
          <p>Batch</p>
          <Field label="">
            <select value={student.batchId || ''} disabled={savingBatch} onChange={(e) => changeBatch(e.target.value)}>
              <option value="">Unassigned</option>
              {batches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <small style={{ color: 'var(--muted)' }}>Tests are assigned per batch</small>
        </Card>
      </div>

      <Card className="table-card">
        <div className="section-title" style={{ padding: '17px 16px 0' }}>
          <div><h2>Test history</h2><p>Most recent first</p></div>
        </div>
        <table>
          <thead><tr><th>Test</th><th>Status</th><th>Score</th><th>Date</th></tr></thead>
          <tbody>
            {attempts.length ? attempts.map((a) => (
              <tr key={a.id} className="clickable" onClick={() => a.quizId && navigate(`/manage-quiz/${a.quizId}`)}>
                <td><b>{a.quizName}</b></td>
                <td><span className={a.status === 'completed' ? 'status' : 'pending'}>{a.status === 'completed' ? 'Completed' : 'In progress'}</span></td>
                <td><b>{formatScore(a.score)}</b>{a.scoreDetails?.correct != null && a.scoreDetails?.totalQuestions != null && <small>{a.scoreDetails.correct}/{a.scoreDetails.totalQuestions} correct</small>}</td>
                <td>{(a.completedAt || a.startedAt) ? new Date(a.completedAt || a.startedAt).toLocaleString() : '—'}</td>
              </tr>
            )) : (
              <tr><td colSpan="4">
                <EmptyState title="No tests taken yet">
                  {student.batchId ? 'Once this student sits a test, it will appear here.' : 'Put this student in a batch so they can receive tests.'}
                </EmptyState>
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </>
  );
}
